/**
 * ClientWorkspaceSmsComposer.jsx
 * v3.21-1794 · In-Workspace generic SMS composer.
 * v3.21-1795 · Recipients are SERVER-AUTHORITATIVE.  The composer now
 * fetches `/api/clients/{client_id}/sms-team-recipients` which
 * resolves canonical Deal Ownership assignment IDs against the
 * current users collection and returns each team member's current
 * name + SMS-capable phone.  Frontend never trusts stale/embedded
 * team-phone values on the Client record.  Suspended / inactive /
 * deleted users are excluded server-side; missing-mobile team
 * members surface as disabled entries per Hub UX conventions.
 *
 * NOT a specialised composer — Financing Result / Welcome /
 * Appointment / ACH / Credit App / Link Delivery remain SEPARATE.
 *
 * Send SSOT:  POST /api/sms/send  (canonical `sms_service`, TextBee).
 * Preview safety gating from v1779 preserved.  Multi-recipient
 * partial-success preserved (one call per recipient, per-recipient
 * ok/error accounting).  Post-send stays in Client Workspace.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Send, X, Loader2, MessageCircle, User, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const _cleanPhoneDigits = (phone) => (phone || '').replace(/\D/g, '');
const _formatPhone = (phone) => {
  const c = _cleanPhoneDigits(phone);
  if (c.length === 10) return `(${c.slice(0,3)}) ${c.slice(3,6)}-${c.slice(6)}`;
  if (c.length === 11 && c.startsWith('1')) return `+1 (${c.slice(1,4)}) ${c.slice(4,7)}-${c.slice(7)}`;
  return phone || '';
};
const _isValidUsMobile = (phone) => {
  const c = _cleanPhoneDigits(phone);
  return c.length === 10 || (c.length === 11 && c.startsWith('1'));
};

export default function ClientWorkspaceSmsComposer({ open, onClose, client, ctx }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [manualPhone, setManualPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState([]);   // v3.21-1795 · server-authoritative
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  const clientId = client?.id;

  // v3.21-1795 · Fetch server-authoritative recipient list every time
  // the composer opens.  Team member names + phones come from the
  // canonical /users records, never from frontend-supplied fields.
  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    (async () => {
      setLoadingRecipients(true);
      try {
        const { data } = await api.get(`/clients/${clientId}/sms-team-recipients`);
        if (!cancelled) setRecipients(Array.isArray(data?.recipients) ? data.recipients : []);
      } catch (e) {
        if (!cancelled) {
          setRecipients([]);
          toast.error(e?.response?.data?.detail || 'Failed to load recipients');
        }
      } finally {
        if (!cancelled) setLoadingRecipients(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, clientId]);

  // Reset form state each time the composer opens; default-select
  // Client 1 once recipients have arrived and Client 1 is selectable.
  useEffect(() => {
    if (!open) return;
    setSelectedIds([]);
    setManualPhone('');
    setMessage('');
    setSending(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const c1 = recipients.find((r) => r.id === 'client_1' && r.selectable);
    if (c1) setSelectedIds([c1.id]);
  }, [open, recipients]);

  // v3.21-1796 · finalTargets useMemo MUST run before the `!open` short
  // circuit below.  Placing `if (!open) return null;` above a hook
  // violates `react-hooks/rules-of-hooks` and fails `craco build`
  // (production ESLint error).  All hooks are declared here; the
  // conditional early-return happens once every hook has been
  // registered.
  const finalTargets = useMemo(() => {
    const selected = recipients.filter((r) => selectedIds.includes(r.id) && r.selectable);
    const targets = selected.map((r) => ({
      phone_number: r.phone,
      display_name: `${r.full_name} · ${r.role}`,
    }));
    const manDigits = _cleanPhoneDigits(manualPhone);
    if (manualPhone && _isValidUsMobile(manDigits)) {
      if (!selected.some((r) => _cleanPhoneDigits(r.phone) === manDigits)) {
        targets.push({
          phone_number: manualPhone,
          display_name: `Manual · ${_formatPhone(manualPhone)}`,
        });
      }
    }
    return targets;
  }, [recipients, selectedIds, manualPhone]);

  const canSend = !sending && finalTargets.length > 0 && (message || '').trim().length > 0;

  if (!open) return null;

  const toggleRecipient = (rid, rec) => {
    if (rec && rec.selectable === false) return;
    setSelectedIds((arr) => arr.includes(rid) ? arr.filter((x) => x !== rid) : [...arr, rid]);
  };

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    const msg = message.trim();
    const results = [];
    await Promise.all(finalTargets.map(async (t) => {
      try {
        await api.post('/sms/send', {
          phone_number: t.phone_number,
          message:      msg,
          client_id:    clientId,
        });
        results.push({ target: t.display_name, ok: true });
      } catch (e) {
        results.push({
          target: t.display_name,
          ok: false,
          error: e?.response?.data?.detail || e?.message || 'send failed',
        });
      }
    }));
    setSending(false);
    const ok = results.filter((r) => r.ok).length;
    const bad = results.length - ok;
    if (bad === 0) {
      toast.success(`SMS sent to ${ok} recipient${ok === 1 ? '' : 's'}.`);
      onClose?.();
    } else if (ok === 0) {
      toast.error(`All ${bad} SMS send${bad === 1 ? '' : 's'} failed.`);
    } else {
      toast.warning(`${ok} sent, ${bad} failed — check timeline for details.`);
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !sending) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-[540px] p-0 overflow-hidden"
        data-testid="client-workspace-sms-composer"
      >
        <DialogHeader className="px-5 py-3 border-b bg-sky-50">
          <DialogTitle className="flex items-center gap-2 text-base">
            <MessageCircle className="h-4 w-4 text-sky-700" />
            Send SMS
          </DialogTitle>
          <DialogDescription className="sr-only">
            Send a generic SMS about this Client while staying inside the Client Workspace. Recipients are resolved server-side from canonical assignments.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Recipients</p>
            {loadingRecipients ? (
              <p className="text-xs italic text-slate-500 flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading recipients…
              </p>
            ) : recipients.length === 0 ? (
              <p className="text-xs italic text-slate-400" data-testid="sms-composer-no-recipients">
                No SMS-capable contacts on file for this Client. Enter a manual number below.
              </p>
            ) : (
              <div
                className="max-h-56 overflow-y-auto border rounded divide-y"
                data-testid="sms-composer-recipient-list"
              >
                {recipients.map((r) => {
                  const disabled = r.selectable === false;
                  return (
                    <label
                      key={r.id}
                      className={`flex items-center gap-2 px-3 py-2 text-sm ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer'}`}
                      data-testid={`sms-composer-recipient-${r.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(r.id)}
                        onChange={() => toggleRecipient(r.id, r)}
                        disabled={disabled}
                        data-testid={`sms-composer-recipient-checkbox-${r.id}`}
                      />
                      <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className="font-medium text-slate-800">{r.full_name || r.role}</span>
                      <span className="text-[10px] uppercase tracking-wide text-slate-400">{r.role}</span>
                      <span className="ml-auto text-xs text-slate-500 font-mono">
                        {disabled ? (r.reason || 'Unavailable') : _formatPhone(r.phone)}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
              <UserPlus className="h-3 w-3" /> Manual phone number (optional)
            </label>
            <Input
              value={manualPhone}
              onChange={(e) => setManualPhone(e.target.value)}
              placeholder="(555) 555-5555"
              className="h-9 text-sm"
              data-testid="sms-composer-manual-phone"
            />
            {manualPhone && !_isValidUsMobile(_cleanPhoneDigits(manualPhone)) && (
              <p className="text-[11px] text-red-600 mt-0.5">Enter a valid 10-digit US mobile.</p>
            )}
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center justify-between">
              <span>Message</span>
              <span className="font-mono text-[10px] text-slate-400">
                {message.length} chars
              </span>
            </p>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Write your message…"
              className="text-sm"
              data-testid="sms-composer-message"
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onClose?.()}
            disabled={sending}
            data-testid="sms-composer-cancel"
          >
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button
            onClick={send}
            disabled={!canSend}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="sms-composer-send"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Send {finalTargets.length > 0 ? `(${finalTargets.length})` : ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
